import type {
    ReleaseNotesAudience,
    ReleaseSettings,
} from '../feature/types/release-notes';

const STORAGE_PREFIX = '7day.releaseNotes';

type StoredAck = {
    releaseId: string;
    acknowledgedAt: number; // ms since epoch
    action: 'dismissed' | 'viewed';
};

function safeGetLocalStorageItem(key: string): string | null {
    try {
        return window.localStorage.getItem(key);
    } catch {
        return null;
    }
}

function safeSetLocalStorageItem(key: string, value: string) {
    try {
        window.localStorage.setItem(key, value);
    } catch {
        // ignore storage failures (private mode, quota, etc.)
    }
}

function parseDateMs(releasedAt: string): number | null {
    const ms = Date.parse(releasedAt);
    return Number.isFinite(ms) ? ms : null;
}

export function getReleasePromptExpiryMs(releasedAt: string): number | null {
    const releasedAtMs = parseDateMs(releasedAt);
    if (releasedAtMs === null) return null;
    const threeWeeksMs = 21 * 24 * 60 * 60 * 1000;
    return releasedAtMs + threeWeeksMs;
}

export function hasAcknowledgedRelease(releaseId: string): boolean {
    const key = `${STORAGE_PREFIX}.ack`;
    const raw = safeGetLocalStorageItem(key);
    if (!raw) return false;
    try {
        const parsed = JSON.parse(raw) as StoredAck;
        return parsed?.releaseId === releaseId;
    } catch {
        return false;
    }
}

export function acknowledgeRelease(
    releaseId: string,
    action: StoredAck['action'],
) {
    const key = `${STORAGE_PREFIX}.ack`;
    const payload: StoredAck = {
        releaseId,
        acknowledgedAt: Date.now(),
        action,
    };
    safeSetLocalStorageItem(key, JSON.stringify(payload));
}

export function getReleaseNotesAudience(
    userRole: string | undefined,
): ReleaseNotesAudience | null {
    const role = String(userRole ?? '').toLowerCase();
    if (!role) return null;
    if (role === 'admin') return 'admin';
    if (role.includes('contractor')) return 'contractor';
    if (role.includes('owner')) return 'owner';
    if (role.includes('guest')) return 'guest';
    return null;
}

export function releaseAppliesToUser(
    config: ReleaseSettings,
    userRole: string | undefined,
): boolean {
    const audiences = config.appliesTo;
    if (!audiences?.length) return true;

    const audience = getReleaseNotesAudience(userRole);
    if (!audience) return false;

    return audiences.includes(audience);
}

export function shouldShowReleasePrompt(
    config: ReleaseSettings,
    userRole?: string,
): boolean {
    if (!config.enabled) return false;
    if (!releaseAppliesToUser(config, userRole)) return false;
    if (hasAcknowledgedRelease(config.releasedAt)) return false;

    const releasedAtMs = parseDateMs(config.releasedAt);
    if (releasedAtMs === null) return false;
    // Don't show until the release date/time has passed.
    if (Date.now() < releasedAtMs) return false;

    const expiryMs = getReleasePromptExpiryMs(config.releasedAt);
    if (expiryMs === null) return false;
    if (Date.now() > expiryMs) return false;

    return true;
}
